import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TicketAPI } from '@base/api/ticket-api';
import type { FieldTicketRequest, UpdateTicketRequest } from '@core/types/ticket';
import { invalidateTickets, invalidateTicketDetail } from './cache-config';
import { queryKeys } from './queries';

const ticketAPI = new TicketAPI();

export function useCreateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: FieldTicketRequest) => ticketAPI.createTicket(data),
    onSuccess: (_, variables) => {
      invalidateTickets(queryClient, variables.ticket_id_external);
    },
  });
}

export function useUpdateTicket() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateTicketRequest) => ticketAPI.updateTicket(data),
    onSuccess: (responseData, variables) => {
      // Directly update the ticket detail cache with the response data.
      // This ensures the detail page shows the correct data immediately
      // (including updated images) without waiting for a refetch.
      queryClient.setQueryData(
        queryKeys.ticket(variables.ticket_id_external),
        responseData
      );
      // Also invalidate the ticket list so it reflects the changes
      invalidateTickets(queryClient, variables.ticket_id_external);
    },
  });
}

export function useDeleteDefectLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticket_id_external,
      defect_line_id,
    }: {
      ticket_id_external: string;
      defect_line_id: string;
      incident_id: string;
    }) => ticketAPI.deleteDefectLine(ticket_id_external, defect_line_id),
    onSuccess: (_, variables) => {
      invalidateTickets(queryClient, variables.incident_id);
      invalidateTicketDetail(queryClient, variables.ticket_id_external);
    },
  });
}
